import { logger } from '@/lib/logger';
import { getProxiedMediaUrl } from '@/lib/media-proxy';
import { getSignedFileUrl } from '@/lib/signedFiles';
import { mixpanelService } from '@/services/mixpanel.service';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { useCallback, useState } from 'react';
import { Linking, Platform } from 'react-native';
import type { LessonMaterial } from '../types';

function getAudioExtension(type: string): string {
  if (type === 'm4a') return '.m4a';
  if (type === 'mp4') return '.mp4';
  if (type === 'mp3' || type === 'audio') return '.mp3';
  return '.mp3';
}

interface Params {
  courseSlug: string;
  lessonSlug: string;
  user: any;
  fullPath: string;
}

export function useMaterialDownload({ courseSlug, lessonSlug, user, fullPath }: Params) {
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  const [downloadingAudioId, setDownloadingAudioId] = useState<number | null>(null);
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null);

  const closePdfViewer = useCallback(() => setViewingPdf(null), []);

  const handlePdfView = useCallback(async (url: string, material: LessonMaterial) => {
    mixpanelService.trackPdfMaterialClick(url, courseSlug, lessonSlug, user, fullPath);

    if (Platform.OS === 'web') {
      // Fetch via the signed URL but show the PDF from a blob: URL, so the
      // credential-carrying URL never appears in the address bar where it
      // could be copied and shared. The tab must be opened synchronously
      // (before any await) or popup blockers eat it.
      const newTab = window.open('', '_blank');
      try {
        // WordPress/S3 hosted files must go through /api/media: fetching them
        // directly fails on CORS (S3) or mixed content (legacy http:// rows).
        const signedUrl = await getSignedFileUrl(getProxiedMediaUrl(url));
        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const blob = await response.blob();
        const pdfBlob = blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        if (newTab) {
          newTab.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        // Revoke once the viewer has had ample time to load the document.
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      } catch (error) {
        newTab?.close();
        logger.error('Failed to view PDF on web:', { url, error });
        Linking.openURL(await getSignedFileUrl(url));
      }
      return;
    }

    // Native: open in the in-app viewer (WKWebView renders PDFs natively on
    // iOS, chromeless, so the signed URL is never user-visible). Android's
    // WebView cannot render PDFs, so it keeps the external-open behavior.
    const signedUrl = await getSignedFileUrl(url);
    if (Platform.OS === 'ios') {
      setViewingPdf({ url: signedUrl, title: material.title });
      return;
    }
    Linking.openURL(signedUrl);
  }, [courseSlug, lessonSlug, user, fullPath]);

  const handleMaterialPress = useCallback(async (material: LessonMaterial) => {
    Linking.openURL(await getSignedFileUrl(material.url));
  }, []);

  const handlePdfDownload = useCallback(async (url: string, material: LessonMaterial) => {
    mixpanelService.trackPdfMaterialClick(url, courseSlug, lessonSlug, user, fullPath);

    if (Platform.OS === 'web') {
      try {
        const fetchUrl = await getSignedFileUrl(getProxiedMediaUrl(url));
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = (material.title.trim() || url.split('/').pop() || 'download') + '.pdf';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        logger.error('Failed to download PDF:', { url, error });
        // Never fall back to the raw URL: for protected files the Worker
        // answers it with the "Upgrade Now" 403 page even for entitled users.
        window.open(await getSignedFileUrl(url), '_blank');
      }
    } else {
      try {
        setDownloadingPdfId(material.id);
        const fileName = (material.title?.trim() || url.split('/').pop() || 'download')
          .replace(/[^a-z0-9]/gi, '_') + '.pdf';
        const fileUri = FileSystem.documentDirectory + fileName;
        const downloadResult = await FileSystem.downloadAsync(await getSignedFileUrl(url), fileUri);
        if (downloadResult.status === 200) {
          const isAvailable = await Sharing.isAvailableAsync();
          if (isAvailable) {
            await Sharing.shareAsync(downloadResult.uri, {
              mimeType: 'application/pdf',
              dialogTitle: `Save ${material.title?.trim() || 'PDF'}`,
              UTI: 'com.adobe.pdf',
            });
          } else {
            await Linking.openURL(downloadResult.uri);
          }
        } else {
          throw new Error(`Download failed with status ${downloadResult.status}`);
        }
      } catch (error: any) {
        logger.error('Failed to download PDF on mobile:', {
          url,
          materialId: material.id,
          error: error?.message || error,
        });
        Linking.openURL(url);
      } finally {
        setDownloadingPdfId(null);
      }
    }
  }, [courseSlug, lessonSlug, user, fullPath]);

  const handleAudioDownload = useCallback(async (url: string, material: LessonMaterial) => {
    mixpanelService.trackPdfMaterialClick(url, courseSlug, lessonSlug, user, fullPath);
    const ext = getAudioExtension(material.type);

    if (Platform.OS === 'web') {
      try {
        const fetchUrl = await getSignedFileUrl(getProxiedMediaUrl(url));
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Failed to fetch audio: ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = (material.title?.trim() || url.split('/').pop() || 'audio').replace(/\.[^.]+$/, '') + ext;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        logger.error('Failed to download audio:', { url, error });
        window.open(await getSignedFileUrl(url), '_blank');
      }
    } else {
      try {
        setDownloadingAudioId(material.id);
        const baseName = (material.title?.trim() || url.split('/').pop() || 'audio').replace(/[^a-z0-9]/gi, '_');
        const fileUri = FileSystem.documentDirectory + baseName + ext;
        const downloadResult = await FileSystem.downloadAsync(
          await getSignedFileUrl(getProxiedMediaUrl(url)),
          fileUri,
        );
        if (downloadResult.status === 200) {
          const isAvailable = await Sharing.isAvailableAsync();
          if (isAvailable) {
            await Sharing.shareAsync(downloadResult.uri, {
              mimeType:
                material.type === 'm4a' || material.type === 'mp4' ? 'audio/mp4' : 'audio/mpeg',
              dialogTitle: `Save ${material.title?.trim() || 'Audio'}`,
              UTI:
                material.type === 'm4a'
                  ? 'public.m4a-audio'
                  : material.type === 'mp4'
                    ? 'public.mpeg-4'
                    : 'public.mp3',
            });
          } else {
            await Linking.openURL(downloadResult.uri);
          }
        } else {
          throw new Error(`Download failed with status ${downloadResult.status}`);
        }
      } catch (error: any) {
        logger.error('Failed to download audio on mobile:', {
          url,
          materialId: material.id,
          error: error?.message || error,
        });
        Linking.openURL(url);
      } finally {
        setDownloadingAudioId(null);
      }
    }
  }, [courseSlug, lessonSlug, user, fullPath]);

  return {
    downloadingPdfId,
    downloadingAudioId,
    viewingPdf,
    closePdfViewer,
    handlePdfView,
    handleMaterialPress,
    handlePdfDownload,
    handleAudioDownload,
  };
}
